import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, Post } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import SubscribeWidget from '../components/SubscribeWidget';

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
}

const categoryColors: Record<string, { bg: string; color: string; circleBg: string }> = {
  gala: { bg: 'var(--red-bg)', color: 'var(--red-primary)', circleBg: 'var(--red-primary)' },
  workshop: { bg: 'var(--yellow-bg)', color: 'var(--yellow-accent)', circleBg: 'var(--yellow-accent)' },
  community: { bg: 'var(--green-bg)', color: 'var(--green-accent)', circleBg: 'var(--green-accent)' },
  education: { bg: 'var(--blue-bg)', color: 'var(--blue-accent)', circleBg: 'var(--blue-accent)' },
  seminar: { bg: 'var(--blue-bg)', color: 'var(--blue-accent)', circleBg: 'var(--blue-accent)' },
  culture: { bg: 'var(--orange-bg)', color: 'var(--orange-accent)', circleBg: 'var(--orange-accent)' },
  advocacy: { bg: 'var(--pink-bg)', color: 'var(--pink-accent)', circleBg: 'var(--pink-accent)' },
};

const fallbackEvents: Post[] = [
  {
    id: 10, title: 'Festive Feast 2026', subtitle: 'Annual Community Gala', slug: 'festive-feast-2026',
    content: 'Join us for CAAOC\'s signature annual gala at Irvine Barclay Theatre with live entertainment and cultural performances.',
    post_type: 'event', status: 'published', featured: 1,
    event_date: '2026-01-30', event_time: '5:30 PM - 9:30 PM', event_location: 'Irvine Barclay Theatre, 4242 Campus Dr, Irvine',
    category: 'gala',
    button1_text: 'Get Tickets', button1_url: '#',
    author_id: 1, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 11, title: 'Festive Feast 2025', subtitle: 'Year of the Snake Gala', slug: 'festive-feast-2025',
    content: 'Annual Lunar New Year Spring Gala at Irvine Barclay Theatre.',
    post_type: 'event', status: 'published', featured: 0,
    event_date: '2025-01-12', event_time: '5:30 PM - 9:30 PM', event_location: 'Irvine Barclay Theatre',
    category: 'gala', author_id: 1, created_at: '2025-01-01T00:00:00Z', updated_at: '2025-01-01T00:00:00Z',
  },
  {
    id: 12, title: 'Y.E.T. Youth Training', subtitle: 'Youth Effectiveness Training', slug: 'yet',
    content: '18-hour social-emotional skills workshop for ages 13-20.',
    post_type: 'event', status: 'published', featured: 0,
    event_date: '2026-03-29', event_time: '9:00 AM - 5:00 PM', event_location: '2040 Main Street, Irvine',
    category: 'workshop', author_id: 1, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 13, title: 'Parent Workshop Series', subtitle: 'Monthly Education', slug: 'parent-workshop',
    content: 'Monthly workshops covering education, parenting, and community resources.',
    post_type: 'event', status: 'published', featured: 0,
    event_date: '2026-04-15', event_time: '10:00 AM - 12:00 PM', event_location: 'Irvine Community Center',
    category: 'education', author_id: 1, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z',
  },
];

function formatDate(dateStr?: string, locale?: string): { month: string; day: string } {
  if (!dateStr) return { month: 'TBD', day: '' };
  const d = new Date(dateStr + 'T00:00:00');
  return {
    month: d.toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-US', { month: 'short' }).toUpperCase(),
    day: String(d.getDate()),
  };
}

export default function EventsPage() {
  const { locale, t, prefix } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<Post[]>(fallbackEvents);
  const initialCategory = searchParams.get('category') || 'All';
  const [activeFilter, setActiveFilter] = useState(initialCategory);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [loading, setLoading] = useState(false);

  const filterTabs = [
    { key: 'All', label: t('events.all') },
    { key: 'gala', label: t('events.annualGala') },
    { key: 'workshop', label: t('events.workshops') },
    { key: 'community', label: t('events.community') },
    { key: 'education', label: t('events.education') },
    { key: 'seminar', label: locale === 'zh' ? '讲座' : 'Seminars' },
    { key: 'culture', label: locale === 'zh' ? '文化' : 'Culture' },
    { key: 'advocacy', label: locale === 'zh' ? '倡导' : 'Advocacy' },
  ];

  useEffect(() => {
    setLoading(true);
    const localeParam = locale === 'zh' ? '&locale=zh' : '&locale=en';
    api.get<{ posts: Post[] }>(`/posts?post_type=event&status=published&limit=50${localeParam}`)
      .then((data) => {
        if (data.posts && data.posts.length > 0) {
          setEvents(data.posts);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [locale]);

  const filtered = activeFilter === 'All' ? events : events.filter((e) => e.category === activeFilter);

  return (
    <div>
      {/* Page hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, var(--red-primary) 0%, var(--red-dark) 100%)',
          color: '#fff',
          padding: '100px 0 60px',
        }}
      >
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '2.8rem', fontWeight: 900, marginBottom: 12 }}>{t('events.title')}</h1>
          <p style={{ fontSize: '1.15rem', opacity: 0.9, maxWidth: 600, margin: '0 auto' }}>
            {t('events.subtitle')}
          </p>
        </div>
      </section>

      {/* Filters + content */}
      <section style={{ padding: 'var(--section-padding)', background: 'var(--gray-100)' }}>
        <div className="container">
          {/* Filter bar */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 32,
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {filterTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 24,
                    border: 'none',
                    background: activeFilter === tab.key ? 'var(--red-primary)' : 'var(--white)',
                    color: activeFilter === tab.key ? '#fff' : 'var(--gray-600)',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    boxShadow: activeFilter === tab.key ? 'var(--shadow-md)' : 'var(--shadow-sm)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => setViewMode('grid')}
                style={{
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--gray-300)',
                  background: viewMode === 'grid' ? 'var(--gray-900)' : 'var(--white)',
                  color: viewMode === 'grid' ? '#fff' : 'var(--gray-600)',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                &#9638; {t('events.grid')}
              </button>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--gray-300)',
                  background: viewMode === 'list' ? 'var(--gray-900)' : 'var(--white)',
                  color: viewMode === 'list' ? '#fff' : 'var(--gray-600)',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                &#9776; {t('events.list')}
              </button>
            </div>
          </div>

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
              <div className="spinner" />
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray-500)' }}>
              {t('events.noEvents')}
            </div>
          )}

          {!loading && viewMode === 'grid' && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: 24,
              }}
            >
              {filtered.map((event) => {
                const dateInfo = formatDate(event.event_date, locale);
                const catStyle = categoryColors[event.category || ''] || categoryColors['community'];
                return (
                  <div key={event.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Link to={`${prefix}/events/${event.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      {event.featured_image_url && (
                        <img
                          src={event.featured_image_url}
                          alt={event.title}
                          style={{
                            width: '100%',
                            height: 180,
                            objectFit: 'cover',
                            borderRadius: 'var(--radius-sm)',
                            marginBottom: 4,
                          }}
                        />
                      )}
                      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                        <div
                          style={{
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            background: catStyle.circleBg,
                            color: '#fff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            lineHeight: 1,
                          }}
                        >
                          <span style={{ fontSize: '0.55rem', fontWeight: 700 }}>{dateInfo.month}</span>
                          <span style={{ fontSize: '1.1rem', fontWeight: 800 }}>{dateInfo.day}</span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: 2 }}>{event.title}</h3>
                          <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                            {event.event_date &&
                              new Date(event.event_date + 'T00:00:00').toLocaleDateString(locale === 'zh' ? 'zh-CN' : 'en-US', {
                                weekday: 'short',
                                month: 'long',
                                day: 'numeric',
                              })}
                            {event.event_time && ` | ${event.event_time}`}
                          </p>
                        </div>
                      </div>
                    </Link>
                    {event.content && (
                      <Link to={`${prefix}/events/${event.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <p style={{ fontSize: '0.88rem', color: 'var(--gray-600)', lineHeight: 1.5 }}>
                          {stripHtml(event.content).length > 140 ? stripHtml(event.content).substring(0, 140) + '...' : stripHtml(event.content)}
                        </p>
                      </Link>
                    )}
                    {event.event_location && (
                      <div style={{ fontSize: '0.83rem', color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>&#128205;</span> {event.event_location}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', flexWrap: 'wrap' }}>
                      {event.ticket_url && (
                        <a href={event.ticket_url} className="btn btn-primary" style={{ padding: '7px 14px', fontSize: '0.82rem' }}>
                          {t('events.getTickets')}
                        </a>
                      )}
                      {event.category && (
                        <button
                          onClick={() => setActiveFilter(event.category!)}
                          className="badge"
                          style={{ background: catStyle.bg, color: catStyle.color, marginLeft: 'auto', border: 'none', cursor: 'pointer' }}
                        >
                          {event.category}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && viewMode === 'list' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filtered.map((event) => {
                const dateInfo = formatDate(event.event_date, locale);
                const catStyle = categoryColors[event.category || ''] || categoryColors['community'];
                return (
                  <div
                    key={event.id}
                    className="card"
                    style={{
                      display: 'flex',
                      gap: 20,
                      alignItems: 'center',
                      padding: '16px 24px',
                    }}
                  >
                    <Link to={`${prefix}/events/${event.slug}`} style={{ display: 'flex', gap: 20, alignItems: 'center', flex: 1, textDecoration: 'none', color: 'inherit' }}>
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: '50%',
                          background: catStyle.circleBg,
                          color: '#fff',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          lineHeight: 1,
                        }}
                      >
                        <span style={{ fontSize: '0.55rem', fontWeight: 700 }}>{dateInfo.month}</span>
                        <span style={{ fontSize: '1.1rem', fontWeight: 800 }}>{dateInfo.day}</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 2 }}>{event.title}</h3>
                        <p style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>
                          {event.event_location && `${event.event_location} | `}
                          {event.event_time}
                        </p>
                      </div>
                    </Link>
                    {event.category && (
                      <button
                        onClick={() => setActiveFilter(event.category!)}
                        className="badge"
                        style={{ background: catStyle.bg, color: catStyle.color, border: 'none', cursor: 'pointer' }}
                      >
                        {event.category}
                      </button>
                    )}
                    {event.ticket_url && (
                      <a href={event.ticket_url} className="btn btn-primary" style={{ padding: '7px 16px', fontSize: '0.82rem' }}>
                        {t('events.tickets')}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Subscribe */}
      <section style={{ padding: '0 0 60px', background: 'var(--gray-100)' }}>
        <div className="container">
          <SubscribeWidget />
        </div>
      </section>
    </div>
  );
}
